import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle, Users, Utensils, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { useMyRsvp, useSubmitRsvp, useDeleteRsvp } from '../../hooks/useRsvp';
import type { RsvpStatus } from '../../types/rsvp';

const RsvpFormSchema = z.object({
  status: z.enum(['attending', 'not_attending', 'maybe']),
  adultCount: z.number().int().min(1, 'At least 1 adult').max(20),
  childCount: z.number().int().min(0).max(20),
  dietaryNeeds: z.string().max(500).optional(),
  message: z.string().max(1000).optional(),
});

type RsvpFormData = z.infer<typeof RsvpFormSchema>;

interface RSVPFormProps {
  eventId: string;
  onSuccess?: () => void;
}

const STATUS_OPTIONS: { value: RsvpStatus; label: string; description: string }[] = [
  { value: 'attending', label: 'Attending', description: "Count me in! I'll be there." },
  { value: 'not_attending', label: 'Not Attending', description: "Unfortunately I can't make it." },
  { value: 'maybe', label: 'Maybe', description: "I'm not sure yet." },
];

export function RSVPForm({ eventId, onSuccess }: RSVPFormProps) {
  const { data: existingRsvp, isLoading: isLoadingRsvp } = useMyRsvp(eventId);
  const submitMutation = useSubmitRsvp();
  const deleteMutation = useDeleteRsvp();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<RsvpFormData>({
    resolver: zodResolver(RsvpFormSchema),
    defaultValues: {
      status: 'attending',
      adultCount: 1,
      childCount: 0,
      dietaryNeeds: '',
      message: '',
    },
  });

  const selectedStatus = watch('status');
  const isAttendingOrMaybe = selectedStatus === 'attending' || selectedStatus === 'maybe';

  useEffect(() => {
    if (existingRsvp?.rsvp) {
      const rsvp = existingRsvp.rsvp!
      reset({
        status: rsvp.status,
        adultCount: rsvp.adultCount,
        childCount: rsvp.childCount,
        dietaryNeeds: rsvp.dietaryNeeds || '',
        message: rsvp.message || '',
      });
    }
  }, [existingRsvp, reset]);

  const onSubmit = (data: RsvpFormData) => {
    submitMutation.mutate({
      eventId,
      status: data.status,
      adultCount: isAttendingOrMaybe ? data.adultCount : 1,
      childCount: isAttendingOrMaybe ? data.childCount : 0,
      dietaryNeeds: isAttendingOrMaybe ? (data.dietaryNeeds || null) : null,
      needsAccommodation: false,
      message: data.message || null,
    }, {
      onSuccess: () => onSuccess?.(),
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate(eventId);
  };

  if (isLoadingRsvp) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-brand" />
        <span className="ml-2 text-text-secondary">Loading your RSVP...</span>
      </div>
    );
  }

  const hasExistingRsvp = !!existingRsvp?.rsvp;
  const isSubmitting = submitMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Success message */}
      {submitMutation.isSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-green-800 text-sm font-medium">
            {hasExistingRsvp ? 'Your RSVP has been updated!' : 'Your RSVP has been submitted!'}
          </p>
        </div>
      )}

      {deleteMutation.isSuccess && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <p className="text-amber-800 text-sm font-medium">Your RSVP has been removed.</p>
        </div>
      )}

      {/* Error message */}
      {(submitMutation.isError || deleteMutation.isError) && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-800 text-sm font-medium">
            Something went wrong. Please try again.
          </p>
        </div>
      )}

      {/* Status Selection */}
      <div>
        <label className="block text-sm font-semibold text-text-primary mb-3">
          Will you be attending?
        </label>
        <div className="space-y-2">
          {STATUS_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                selectedStatus === option.value
                  ? 'border-brand bg-brand-light/30'
                  : 'border-border bg-surface-0 hover:bg-surface-1'
              }`}
            >
              <input
                type="radio"
                value={option.value}
                {...register('status')}
                className="w-4 h-4 text-brand focus:ring-brand"
              />
              <div>
                <p className="font-medium text-text-primary">{option.label}</p>
                <p className="text-sm text-text-tertiary">{option.description}</p>
              </div>
            </label>
          ))}
        </div>
        {errors.status && (
          <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
        )}
      </div>

      {/* Conditional detail fields - only show for attending/maybe */}
      {isAttendingOrMaybe && (
        <>
          {/* Guest Counts */}
          <div className="bg-surface-0 rounded-xl border border-border p-4 space-y-4">
            <div className="flex items-center gap-2 text-text-primary font-semibold">
              <Users className="w-4 h-4" />
              <span>Guest Count</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="adultCount" className="block text-sm font-medium text-text-secondary mb-1">
                  Adults
                </label>
                <input
                  id="adultCount"
                  type="number"
                  min={1}
                  max={20}
                  {...register('adultCount', { valueAsNumber: true })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-surface-0 text-text-primary focus:ring-2 focus:ring-brand focus:border-brand"
                />
                {errors.adultCount && (
                  <p className="mt-1 text-xs text-red-600">{errors.adultCount.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="childCount" className="block text-sm font-medium text-text-secondary mb-1">
                  Children
                </label>
                <input
                  id="childCount"
                  type="number"
                  min={0}
                  max={20}
                  {...register('childCount', { valueAsNumber: true })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-surface-0 text-text-primary focus:ring-2 focus:ring-brand focus:border-brand"
                />
                {errors.childCount && (
                  <p className="mt-1 text-xs text-red-600">{errors.childCount.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Dietary Needs */}
          <div className="bg-surface-0 rounded-xl border border-border p-4 space-y-3">
            <div className="flex items-center gap-2 text-text-primary font-semibold">
              <Utensils className="w-4 h-4" />
              <span>Dietary Needs</span>
            </div>
            <textarea
              {...register('dietaryNeeds')}
              placeholder="Any allergies, vegetarian, vegan, gluten-free, etc."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface-0 text-text-primary placeholder:text-text-tertiary focus:ring-2 focus:ring-brand focus:border-brand resize-none"
            />
            {errors.dietaryNeeds && (
              <p className="mt-1 text-xs text-red-600">{errors.dietaryNeeds.message}</p>
            )}
          </div>

        </>
      )}

      {/* Message */}
      <div className="bg-surface-0 rounded-xl border border-border p-4 space-y-3">
        <div className="flex items-center gap-2 text-text-primary font-semibold">
          <MessageSquare className="w-4 h-4" />
          <span>Message (Optional)</span>
        </div>
        <textarea
          {...register('message')}
          placeholder="Any message for the planning committee..."
          rows={2}
          className="w-full px-3 py-2 rounded-lg border border-border bg-surface-0 text-text-primary placeholder:text-text-tertiary focus:ring-2 focus:ring-brand focus:border-brand resize-none"
        />
        {errors.message && (
          <p className="mt-1 text-xs text-red-600">{errors.message.message}</p>
        )}
      </div>

      {/* Submit / Delete buttons */}
      <div className="space-y-3">
        <Button
          type="submit"
          variant="heritage"
          className="w-full py-3 text-lg font-bold"
          disabled={isSubmitting || isDeleting || (!isDirty && hasExistingRsvp)}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {hasExistingRsvp ? 'Updating...' : 'Submitting...'}
            </span>
          ) : hasExistingRsvp ? (
            'Update RSVP'
          ) : (
            'Submit RSVP'
          )}
        </Button>

        {hasExistingRsvp && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting || isSubmitting}
            className="w-full py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            {isDeleting ? 'Removing...' : 'Remove my RSVP'}
          </button>
        )}
      </div>
    </form>
  );
}
